import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import UploadImageS3VM, { IDefaultImage } from '../../../../../shared/upload-image/upload-image-to-s3.viewmodel';
import { calcByteCodeSize } from '../../../../../../../../infrastructure/utils/common.utils';
import { LONG_PLAIN_TEXT_LENGTH } from '../../../../../../../../constants/common.constants';
import { CO_PILOT_MESSAGE_REQUIRED, CO_PILOT_MESSAGE_TOO_LONG } from '../../../../../_meetings-list/_meeting-co-pilot/_meeting-co-pilot-modal/meeting-co-pilot.texts';
import { PROMPT_TEXT_AREA_LABEL_TEXT } from './upload-image-to-s3-ai.texts';
import { IErrorParsing } from '../../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../../global/modal/alerts-host/alert-templates';
import { AIFeatureDAO } from '../../../../../../../../api/implementations/ai-feature.dao';
import TrackingServiceProvider from '../../../../../../../../tracking-service/tracking-service-provider';
import { AI_FEATURE_TRACKING_EVENTS } from '../../../../../../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../../../../../../infrastructure/global-storage';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../../../../../infrastructure/utils/appsync.utils';
import { IOnPassportBackgroundGenerationCompleted, IOnPassportBackgroundGenerationCompletedResponse } from '../../../../../../../../api/ai-feature.dao';
import { UploadPathKey } from '../../../../../../../../api/file-to-s3.dao';
import { onPassportBackgroundGenerationCompleted } from '../../../../../../../../graphql/subscriptions';

// Resolutions supported by dalle: ['1024x1024', '1792x1024', '1024x1792']
const defaultRequestParams = {
    resolution: '1792x1024',
    images: 1
};

const IMAGE_NAME = 'generated-passport-background.png';

class UploadImageS3WithAIViewModel extends UploadImageS3VM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    @mobx.observable
    private isPromptModalOpen: boolean;
    @mobx.observable
    private prompt: string;
    @mobx.observable
    private isLoading: boolean;
    @mobx.observable
    private imgUrl: string;
    @mobx.observable
    private objectKey: string;
    @mobx.observable
    private isFormChecked: boolean;
    private timeout: NodeJS.Timeout;

    private resolution: string;
    private images: number;
    private subscription: IAppsyncSubscription;

    constructor(
        protected readonly accountId: number,
        protected readonly eventId: number,
        protected readonly imageName: string,
        protected readonly pathKey: UploadPathKey,
        public readonly imageDescription?: string,
        public readonly defaultImage?: IDefaultImage,
        public readonly exceptSVG?: boolean,
        @IoC.Inject private readonly aiFeatureDAO: AIFeatureDAO = IoC.Container.get(AIFeatureDAO),
        @IoC.Inject protected readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject protected readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        super(accountId, eventId, imageName, pathKey, imageDescription, defaultImage, exceptSVG);

        this.initialize();
    }

    @mobx.action
    private initialize = (): void => {
        this.isPromptModalOpen = false;
        this.prompt = '';
        this.isLoading = false;
        this.imgUrl = '';
        this.resolution = defaultRequestParams.resolution;
        this.images = defaultRequestParams.images;
    };

    public getIsPromptModalOpen = (): boolean => {
        return this.isPromptModalOpen;
    };

    @mobx.action
    public openPromptModal = (): void => {
        this.isPromptModalOpen = true;
    };

    @mobx.action
    public closePromptModal = (): void => {
        this.isPromptModalOpen = false;
        this.cleanup();
    };

    public getPrompt = (): string => {
        return this.prompt;
    };

    @mobx.action
    public setPrompt = (value: string): void => {
        this.prompt = value;
    };

    public getIsLoading = (): boolean => {
        return this.isLoading;
    };

    public getImgUrl = (): string => {
        return this.imgUrl;
    };

    @mobx.action
    public setImgUrl = (value: string): void => {
        this.imgUrl = value;
    };

    @mobx.action
    public setObjectKey = (value: string): void => {
        this.objectKey = value;
    };

    public showValidationError(): boolean {
        if (!this.prompt.trim()) {
            return this.isFormChecked;
        }
    }

    public getRequiredPromptError(): string | null {
        return this.intlProvider.getIntl().formatMessage(CO_PILOT_MESSAGE_REQUIRED, {
            field: PROMPT_TEXT_AREA_LABEL_TEXT.defaultMessage
        });
    }

    @mobx.action
    public sendMessageToAI = async (): Promise<void> => {
        this.isLoading = true;

        try {
            await this.aiFeatureDAO.generatePassportBackground({
                accountId: this.accountId,
                eventId: this.eventId,
                prompt: this.prompt,
                size: this.resolution,
                images: this.images
            });

            this.trackEvent(AI_FEATURE_TRACKING_EVENTS.GENERATE_AI_IMAGE);
        } catch (e) {
            UploadImageS3WithAIViewModel.handleApiException(e);
        }
    };

    @mobx.action
    public useImage = (): void => {
        this.deleteImage();
        this.addImage(this.imgUrl, IMAGE_NAME, this.objectKey);
        this.closePromptModal();
        this.trackEvent(AI_FEATURE_TRACKING_EVENTS.USE_AI_IMAGE);
    };

    @mobx.action
    public allowToShowValidationError(): void {
        this.isFormChecked = true;
    }

    public getTooLongPromptError = (): string | null => {
        return calcByteCodeSize(this.prompt) > LONG_PLAIN_TEXT_LENGTH
            ? this.intlProvider.getIntl().formatMessage(CO_PILOT_MESSAGE_TOO_LONG)
            : null;
    };

    public tryAgain = (): void => {
        this.setImgUrl('');
        this.trackEvent(AI_FEATURE_TRACKING_EVENTS.AI_IMAGE_GENERATION_TRY_AGAIN);
    };

    public trackEvent = (event: AI_FEATURE_TRACKING_EVENTS): void => {
        this.trackingService.get().track(event, {
            'Account ID': this.accountId,
            'Account Name': this.globalStorage.getAccount()?.name,
            'Event ID': this.eventId,
            'Event Name': this.globalStorage.getEvent()?.name.toString(),
        });
    };

    public async subscribe(): Promise<void> {
        this.subscription = await subscribeAppsyncData<IOnPassportBackgroundGenerationCompletedResponse>(
            onPassportBackgroundGenerationCompleted,
            { eventId: this.eventId },
            response => {
                const { value: { data } } = response;
                this.onSubscriptionDataHandler(data.onPassportBackgroundGenerationCompleted);
            },
            () => this.subscribe()
        );
    }

    private onSubscriptionDataHandler = (data: IOnPassportBackgroundGenerationCompleted): void => {
        if (data) {
            const { imageUrl, objectKey } = data;
            this.setImgUrl(imageUrl);
            this.setObjectKey(objectKey);

            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };
    @mobx.action
    public unsubscribe(): void {
        this.subscription?.unsubscribe();
        this.isLoading = false;
    }

    private cleanup = (): void => {
        this.imgUrl = '';
        this.isLoading = false;
        this.prompt = '';
        clearTimeout(this.timeout);
    };
}

export { UploadImageS3WithAIViewModel };